import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useAimdoc } from '@aimdoc/sdk-react';
import useApi from '../api';
import { toast } from 'react-toastify';

const ADMIN_ACTIVE_ORG_STORAGE_KEY = 'aimdocAdminActiveOrganizationId';

interface User {
  email: string;
  id: string;
  is_active?: boolean;
  is_admin?: boolean;
  first_name?: string;
  last_name?: string;
  role?: string;
  organization_id?: string;
  username?: string;
  walkthrough_complete?: boolean;
  was_invited?: boolean;
  profile_photo_url?: string | null;
}

interface AuthProfileNames {
  first_name?: string;
  last_name?: string;
}

const normalizeNamePart = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const getAuthProfileNames = (sessionUser: any): AuthProfileNames => {
  const metadata = sessionUser?.user_metadata ?? {};
  const firstName = normalizeNamePart(
    metadata.first_name ?? metadata.given_name ?? metadata.name?.split?.(' ')?.[0]
  );
  const lastNameFromMetadata = normalizeNamePart(metadata.last_name ?? metadata.family_name);

  if (lastNameFromMetadata) {
    return { first_name: firstName, last_name: lastNameFromMetadata };
  }

  const fullName = normalizeNamePart(metadata.full_name ?? metadata.name);
  if (fullName) {
    const [parsedFirstName, ...remainingParts] = fullName.split(/\s+/);
    return {
      first_name: firstName ?? normalizeNamePart(parsedFirstName),
      last_name: normalizeNamePart(remainingParts.join(' ')),
    };
  }

  return { first_name: firstName, last_name: undefined };
};

export interface Organization {
  id: string;
  name: string;
  license_type: string;
  trial_end_date: string | null;
  operator: boolean;
  has_subscription?: boolean;
  cancel_scheduled?: boolean;
  settings: {
    interaction_notification: boolean;
    new_lead_notification: boolean;
    account_research?: { enabled?: boolean; notify_threshold?: number };
  };
}

interface OnboardingDecision {
  requires_decision: boolean;
  email_domain?: string | null;
  organization?: {
    id: string;
    name: string;
  } | null;
  access_request_status?: string | null;
}

export type { OnboardingState as OrganizationOnboardingState } from '../onboarding/types';
import type { OnboardingState as OrganizationOnboardingState } from '../onboarding/types';

export type BootstrapPhase =
  | 'checking_session'
  | 'provisioning_user'
  | 'resolving_organization'
  | 'resolving_organization_choice'
  | 'resolving_onboarding'
  | 'ready'
  | 'unauthenticated'
  | 'error';

interface UserContextType {
  user: User | null;
  organization: Organization | null;
  onboardingDecision: OnboardingDecision | null;
  organizationOnboarding: OrganizationOnboardingState | null;
  bootstrapPhase: BootstrapPhase;
  bootstrapError: string | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  refreshOrganization: () => Promise<void>;
  refreshOrganizationOnboarding: () => Promise<OrganizationOnboardingState | null>;
  refreshOnboardingDecision: () => Promise<void>;
  checkUser: () => Promise<void>;
  checkLicenseType: (license_type: string) => boolean;
  setOrg: (organization: Organization) => void;
  setAdmin: (user: User) => void;
  setOrganizationOnboarding: React.Dispatch<React.SetStateAction<OrganizationOnboardingState | null>>;
  refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const clearStoredSessionState = useCallback(() => {
    localStorage.removeItem('aimdocAdmin');
    localStorage.removeItem('aimdocOrganization');
    localStorage.removeItem('aimdocOnboardingDecision');
    localStorage.removeItem(ADMIN_ACTIVE_ORG_STORAGE_KEY);
  }, []);

  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('aimdocAdmin');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [organization, setOrganization] = useState<Organization | null>(() => {
    const storedOrg = localStorage.getItem('aimdocOrganization');
    return storedOrg ? JSON.parse(storedOrg) : null;
  });
  const [onboardingDecision, setOnboardingDecision] = useState<OnboardingDecision | null>(() => {
    const storedDecision = localStorage.getItem('aimdocOnboardingDecision');
    return storedDecision ? JSON.parse(storedDecision) : null;
  });
  const [organizationOnboarding, setOrganizationOnboarding] = useState<OrganizationOnboardingState | null>(null);
  const [bootstrapPhase, setBootstrapPhase] = useState<BootstrapPhase>('checking_session');
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const loading = !['ready', 'unauthenticated', 'error'].includes(bootstrapPhase);
  const { get, post, put } = useApi();
  const { identify } = useAimdoc();
  const isCheckingUser = useRef(false);
  const pendingCheckUser = useRef(false);
  const checkUserRef = useRef<() => Promise<void>>(async () => {});
  const userRef = useRef<User | null>(user);
  const organizationRef = useRef<Organization | null>(organization);
  const bootstrapPhaseRef = useRef<BootstrapPhase>(bootstrapPhase);
  userRef.current = user;
  organizationRef.current = organization;
  bootstrapPhaseRef.current = bootstrapPhase;
  const getRef = useRef(get);
  const postRef = useRef(post);
  const putRef = useRef(put);
  getRef.current = get;
  postRef.current = post;
  putRef.current = put;

  const refreshOnboardingDecision = useCallback(async () => {
    try {
      const response = await getRef.current('/users/me/onboarding-decision');
      setOnboardingDecision(response.data);
      localStorage.setItem('aimdocOnboardingDecision', JSON.stringify(response.data));
    } catch (error) {
      setOnboardingDecision(null);
      localStorage.removeItem('aimdocOnboardingDecision');
    }
  }, []);

  const refreshOrganizationOnboarding = useCallback(async () => {
    try {
      const response = await getRef.current('/organization-onboarding');
      setOrganizationOnboarding(response.data);
      return response.data as OrganizationOnboardingState;
    } catch (error) {
      console.error('Failed to refresh organization onboarding:', error);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await getRef.current(`/users/me`);
      setUser(response.data);
      localStorage.setItem('aimdocAdmin', JSON.stringify(response.data));
    } catch (error: any) {
      console.error('Error fetching user:', error);
    }
  }, []);

  const refreshOrganization = useCallback(async () => {
    try {
      const response = await getRef.current(`/users/me/organization`);
      setOrganization(response.data);
      localStorage.setItem('aimdocOrganization', JSON.stringify(response.data));
      setOnboardingDecision({ requires_decision: false });
      localStorage.setItem('aimdocOnboardingDecision', JSON.stringify({ requires_decision: false }));
    } catch (error: any) {
      setOrganization(null);
      setOrganizationOnboarding(null);
      localStorage.removeItem('aimdocOrganization');
      if (error?.detail !== 'User or organization not found' && error?.detail !== 'Organization not found') {
        console.error('Error fetching organization:', error);
      }
      await refreshOnboardingDecision();
    }
  }, [refreshOnboardingDecision]);

  const checkUser = useCallback(async () => {
    if (isCheckingUser.current) {
      pendingCheckUser.current = true;
      return;
    }

    isCheckingUser.current = true;
    // If the workspace is already `ready`, this is a re-check (e.g. Supabase
    // emits SIGNED_IN after a token refresh on tab focus), not a cold
    // bootstrap. Don't drop back into the intermediate loading phases: that
    // flips `loading` to true and Main swaps the whole page for the loader,
    // unmounting every screen and discarding in-progress state. Terminal
    // phases (ready / unauthenticated / error) still apply.
    const silentRecheck = bootstrapPhaseRef.current === 'ready';
    const setPhase = (phase: BootstrapPhase) => {
      if (!silentRecheck || ['ready', 'unauthenticated', 'error'].includes(phase)) {
        setBootstrapPhase(phase);
      }
    };
    setPhase('checking_session');
    setBootstrapError(null);
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      if (session) {
        setPhase('provisioning_user');
        const profileNames = getAuthProfileNames(session.user);
        let resolvedUser: User;
        try {
          const userResponse = await getRef.current(`/users/me`);
          let aimdocUser = userResponse.data;
          const missingNameUpdate = {
            email: aimdocUser.email,
            ...(!aimdocUser.first_name && profileNames.first_name ? { first_name: profileNames.first_name } : {}),
            ...(!aimdocUser.last_name && profileNames.last_name ? { last_name: profileNames.last_name } : {}),
          };

          if (Object.keys(missingNameUpdate).length > 1) {
            try {
              const updatedUserResponse = await putRef.current('/users/me', missingNameUpdate);
              aimdocUser = updatedUserResponse.data;
            } catch (updateProfileError) {
              console.error('Error backfilling auth profile names:', updateProfileError);
            }
          }

          setUser(aimdocUser);
          resolvedUser = aimdocUser;
          localStorage.setItem('aimdocAdmin', JSON.stringify(aimdocUser));

          // Keep server-side admin switcher target in sync with localStorage so
          // in-app agent MCP minting can emit acting_on for the viewed org.
          if (aimdocUser?.is_admin) {
            const activeOrganizationId = localStorage.getItem(ADMIN_ACTIVE_ORG_STORAGE_KEY);
            try {
              await putRef.current('/users/me/admin-active-organization', {
                organization_id: activeOrganizationId || null,
              });
            } catch (syncError) {
              console.error('Error syncing admin active organization:', syncError);
            }
          }
        } catch (getUserError: any) {
          if (getUserError.detail === 'User not found') {
            try {
              const inviteId = localStorage.getItem('pendingInviteId');
              const aimdocUser = await postRef.current('/users/', {
                auth_id: session.user.id,
                email: session.user.email,
                username: session.user.email,
                ...profileNames,
                ...(inviteId && { invite_id: inviteId })
              });
              setUser(aimdocUser.data);
              resolvedUser = aimdocUser.data;
              localStorage.setItem('aimdocAdmin', JSON.stringify(aimdocUser.data));
              if (inviteId) localStorage.removeItem('pendingInviteId');
            } catch (createUserError: any) {
              // Check if error is about work email requirement
              if (createUserError.detail && 
                  (createUserError.detail.includes('work email') || 
                   createUserError.detail.includes('Free email providers'))) {
                // Sign out the user since they can't proceed with a free email
                await supabase.auth.signOut();
                setUser(null);
                setOrganization(null);
                setOrganizationOnboarding(null);
                clearStoredSessionState();
                setBootstrapPhase('unauthenticated');
                isCheckingUser.current = false;
                
                // Redirect to login page with error message
                window.location.href = '/login?error=work_email_required';
                return;
              }
              // If it's a different error, re-throw it
              throw createUserError;
            }
          } else {
            throw getUserError;
          }
        }

        setPhase('resolving_organization');
        let orgLoaded = false;
        let resolvedOrganization: Organization | null = null;
        try {
          const orgResponse = await getRef.current(`/users/me/organization`);
          resolvedOrganization = orgResponse.data;
          setOrganization(resolvedOrganization);
          localStorage.setItem('aimdocOrganization', JSON.stringify(resolvedOrganization));
          setOnboardingDecision({ requires_decision: false });
          localStorage.setItem('aimdocOnboardingDecision', JSON.stringify({ requires_decision: false }));
          orgLoaded = true;
        } catch (error: any) {
          setOrganization(null);
          localStorage.removeItem('aimdocOrganization');
          if (error?.detail !== 'User or organization not found' && error?.detail !== 'Organization not found') {
            console.error('Error fetching organization:', error);
          }
        }

        if (orgLoaded) {
          setPhase('resolving_onboarding');
          const onboardingResponse = await getRef.current('/organization-onboarding');
          setOrganizationOnboarding(onboardingResponse.data);
        } else {
          setOrganizationOnboarding(null);
          setPhase('resolving_organization_choice');
          try {
            const onboardingResponse = await getRef.current('/users/me/onboarding-decision');
            setOnboardingDecision(onboardingResponse.data);
            localStorage.setItem('aimdocOnboardingDecision', JSON.stringify(onboardingResponse.data));
          } catch (onboardingError) {
            setOnboardingDecision(null);
            localStorage.removeItem('aimdocOnboardingDecision');
          }
        }

        // Identify user to Aimdoc widget. The server signs the identity
        // (identity verification) and builds the account payload — including
        // the lifecycle stage claim inferred from billing state — which must
        // be forwarded verbatim because the hash covers it byte-for-byte. If
        // the hash endpoint fails we still identify unsigned rather than not
        // at all (account omitted: unsigned account identifies are rejected).
        if (resolvedUser) {
          let userHash: string | undefined;
          let account: { external_id: string; name?: string; stage?: string } | undefined;
          let tenant:
            | { external_id: string; name?: string; account_external_id?: string }
            | undefined;
          let membership: { status?: string; roles?: string[] } | undefined;
          try {
            const identityResponse = await getRef.current('/users/me/aimdoc-identity');
            userHash = identityResponse.data?.user_hash ?? undefined;
            const signedAccount = identityResponse.data?.account;
            if (userHash && signedAccount?.external_id) {
              account = {
                external_id: signedAccount.external_id,
                name: signedAccount.name ?? undefined,
                stage: signedAccount.stage ?? undefined,
              };
            }
            // v5 runtime claims (workspace + role). Forwarded verbatim: the
            // hash covers them byte-for-byte, so any local edit breaks it.
            const signedTenant = identityResponse.data?.tenant;
            if (userHash && signedTenant?.external_id) {
              tenant = {
                external_id: signedTenant.external_id,
                name: signedTenant.name ?? undefined,
                account_external_id: signedTenant.account_external_id ?? undefined,
              };
              const signedMembership = identityResponse.data?.membership;
              if (signedMembership) {
                membership = {
                  status: signedMembership.status ?? undefined,
                  roles: signedMembership.roles ?? undefined,
                };
              }
            }
          } catch (error) {
            console.error('Error fetching Aimdoc identity hash:', error);
          }
          identify({
            external_id: resolvedUser.id,
            email: resolvedUser.email,
            // Not covered by the user_hash (which signs external_id+email and
            // the account claims), so safe to attach client-side. Lets the
            // widget greet by name without a server round-trip.
            first_name: resolvedUser.first_name ?? undefined,
            last_name: resolvedUser.last_name ?? undefined,
            // Prefer the server-signed home-org name: organizationRef holds
            // the admin org-switcher's ACTIVE org, not who the user is.
            company: account?.name ?? resolvedOrganization?.name,
            account,
            tenant,
            membership,
            user_hash: userHash,
          } as any);
        }

        setBootstrapPhase('ready');
      } else {
        setUser(null);
        setOrganization(null);
        setOnboardingDecision(null);
        setOrganizationOnboarding(null);
        clearStoredSessionState();
        setBootstrapPhase('unauthenticated');
      }
    } catch (error: any) {
      console.error('Error in checkUser:', error);
      setBootstrapError(error?.detail || error?.message || 'Unable to load your Aimdoc workspace.');
      setBootstrapPhase('error');
    } finally {
      isCheckingUser.current = false;
      if (pendingCheckUser.current) {
        pendingCheckUser.current = false;
        window.setTimeout(() => checkUserRef.current(), 0);
      }
    }
  }, [clearStoredSessionState]);
  checkUserRef.current = checkUser;

  useEffect(() => {
    checkUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Supabase re-emits SIGNED_IN whenever it refreshes or recovers the
      // session — notably every time the tab regains visibility — so only
      // treat it as a bootstrap trigger when no user is loaded yet. For an
      // already-loaded user the 5-minute refresh interval below is enough.
      if (event === 'SIGNED_OUT' || (event === 'SIGNED_IN' && !userRef.current)) {
        // Supabase dispatches auth changes while holding its client lock.
        // Starting getSession() synchronously from this callback can wait on
        // that same lock, leaving new signups stuck before API provisioning.
        window.setTimeout(() => checkUserRef.current(), 0);
      }
    });

    const refreshInterval = setInterval(() => {
      if (userRef.current) {
        refreshUser();
        refreshOrganization();
      }
    }, 5 * 60 * 1000);

    return () => {
      authListener.subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [checkUser, refreshOrganization, refreshUser]);

  const setOrg = useCallback((organization: Organization) => {
    setOrganization(organization);
    localStorage.setItem('aimdocOrganization', JSON.stringify(organization));
  }, []);

  const setAdmin = useCallback((user: User) => {
    setUser(user);
    localStorage.setItem('aimdocAdmin', JSON.stringify(user));
  }, []);

  // Feature gates by license tier are retired (2026-08-20): every org gets
  // every feature; usage is gated by credits. The only thing this still
  // answers is "does this org exist?". The `license_type` argument is kept
  // so the ~50 call sites don't churn; it no longer influences the answer.
  const checkLicenseType = useCallback((_license_type: string) => {
    // Nothing is date-gated any more (credit enforcement, PR C): an org that
    // exists has every feature; running out of credits pauses its agents
    // server-side and is surfaced by the credit banner, not by hiding UI.
    return !!organization;
  }, [organization]);

  const refresh = useCallback(async () => {
    try {
      const refreshes: Promise<unknown>[] = [
        refreshUser(),
        refreshOrganization(),
        refreshOnboardingDecision()
      ];
      if (organizationRef.current) {
        refreshes.push(refreshOrganizationOnboarding());
      }
      await Promise.all(refreshes);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, [refreshOnboardingDecision, refreshOrganization, refreshOrganizationOnboarding, refreshUser]);

  const contextValue = useMemo(() => ({
    user,
    checkUser,
    loading,
    organization,
    onboardingDecision,
    organizationOnboarding,
    bootstrapPhase,
    bootstrapError,
    refreshUser,
    refreshOrganization,
    refreshOrganizationOnboarding,
    refreshOnboardingDecision,
    checkLicenseType,
    setOrg,
    setAdmin,
    setOrganizationOnboarding,
    refresh
  }), [
    user,
    checkUser,
    loading,
    organization,
    onboardingDecision,
    organizationOnboarding,
    bootstrapPhase,
    bootstrapError,
    refreshUser,
    refreshOrganization,
    refreshOrganizationOnboarding,
    refreshOnboardingDecision,
    checkLicenseType,
    setOrg,
    setAdmin,
    setOrganizationOnboarding,
    refresh
  ]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
